/**
 * Item and order status utilities
 */

import {
  ItemStatus,
  InputType,
  ItemStatusStrings,
  ItemStatusClasses,
  FulfillmentStatus,
  FulfillmentStatusStrings,
  FulfillmentStatusClasses,
  PaymentStatusStrings,
  PaymentStatusClasses,
  ItemPendingStatus,
  ItemPendingStatusStrings,
  BillingCycleType,
  SubscriptionDisplayType,
  // ProcessType
} from '../constants'

import type { OrderItem, Order } from '../types'

/**
 * Check if item has initial hold status
 */
export function isInitialHoldStatus(item: OrderItem): boolean {
  if (item.status === ItemStatus.Pending) {
    return true
  } else if (item.status === ItemStatus.Paused) {
    return false // we always show paused subscription
  }
  // Digital (non-shippable) trials have no shipment, so the fulfillment-status
  // check never fires — treat them as initial hold when they can be cancelled.
  if (!(item as any).is_shippable && (item as any).can_cancel) {
    return true
  }
  const fulfillmentStatus = (item as any).fulfillment_status
  return (
    (item as any).billing_cycle === 1 &&
    (fulfillmentStatus === FulfillmentStatus.Hold || fulfillmentStatus === FulfillmentStatus.Frozen)
  )
}

/**
 * Check if item is recurring
 */
export function isRecurring(item: OrderItem): boolean {
  return item.campaign_product?.billing_cycle_type === BillingCycleType.Recurring
}

/**
 * Check if subscription should be shown for item
 */
export function showSubscription(item: OrderItem): boolean {
  if (!item.campaign_product) {
    return false
  }

  const { subscription_display_type, billing_cycle_type } = item.campaign_product

  if (billing_cycle_type === BillingCycleType.Recurring) {
    if (subscription_display_type === SubscriptionDisplayType.Never) {
      return false
    }
    if (subscription_display_type === SubscriptionDisplayType.AfterFirstCycle) {
      // Logic to check if after first cycle would go here
      return item.status === ItemStatus.Active
    }
    return true
  }

  return false
}

/**
 * Check if fulfillment should be shown for item
 */
export function showFulfillment(item: OrderItem): boolean {
  return item.campaign_product?.fulfillment_type !== 0 // Assuming 0 is NoShipping
}

/**
 * Get item status string with optional subscription info
 */
export function getItemStatus(
  item: OrderItem,
  statusLabels: Record<string, string> = {},
  appendSubscription: boolean = true
): string {
  if (!item) {
    return ''
  }

  let status = statusLabels[item.status] || ItemStatusStrings[item.status] || 'Unknown'

  if (appendSubscription && showSubscription(item)) {
    status += ' (Subscription)'
  }

  return status
}

/**
 * Get item status CSS class
 */
export function getItemStatusClass(item: OrderItem): string {
  return ItemStatusClasses[item.status] || 'pending'
}

export function getPaymentStatus(order: Order): string {
  return PaymentStatusStrings[order.payment_status ?? -1] || 'Unknown'
}

export function getPaymentStatusClass(order: Order): string {
  return PaymentStatusClasses[order.payment_status ?? -1] || 'pending'
}

function getPendingFulfillmentStatus(item: OrderItem): ItemPendingStatus {
  if ((item as any).pending_statuses) {
    for (const ps of (item as any).pending_statuses) {
      if (ps !== ItemPendingStatus.CancelSubscription) {
        return ps
      }
    }
  }
  return ItemPendingStatus.NotPending
}

export function getItemFulfillmentStatus(item: OrderItem): string {
  const pendingStatus = getPendingFulfillmentStatus(item)
  if (pendingStatus !== ItemPendingStatus.NotPending) {
    return ItemPendingStatusStrings[pendingStatus] || 'Pending'
  }

  if (item.status === ItemStatus.Declined) {
    return 'Declined'
  }

  if (
    (item.status === ItemStatus.Cancelled || item.status === ItemStatus.ThirdPartyCancel) &&
    (item as any).fulfillment_status === FulfillmentStatus.None
  ) {
    return 'Cancelled'
  }

  return FulfillmentStatusStrings[(item as any).fulfillment_status] || 'Unknown'
}

export function getItemFulfillmentStatusClass(item: OrderItem): string {
  if (getPendingFulfillmentStatus(item) !== ItemPendingStatus.NotPending) {
    return 'pending'
  }
  if (item.status === ItemStatus.Declined) {
    return 'cancelled'
  }

  if (
    (item.status === ItemStatus.Cancelled || item.status === ItemStatus.ThirdPartyCancel) &&
    (item as any).fulfillment_status === FulfillmentStatus.None
  ) {
    return 'cancelled'
  }

  return FulfillmentStatusClasses[(item as any).fulfillment_status] || 'pending'
}

/**
 * Sort items by status and date
 */
export function sortItems<T extends OrderItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    // Sort by status first, then by creation date
    if (a.status !== b.status) {
      return a.status - b.status
    }

    if ((a.order as any)?.created && (b.order as any)?.created) {
      return new Date((b.order as any).created).getTime() - new Date((a.order as any).created).getTime()
    }

    return 0
  })
}

/**
 * Check if customer is a test customer
 */
export function isTestCustomer(customer: any): boolean {
  if (!customer?.email) {
    return false
  }

  return (
    customer.email.includes('test') || customer.email.includes('@example.com') || customer.email.includes('@test.com')
  )
}

/**
 * Check if card number is a test card
 */
export function isTestCard(cardNumber: string, _session?: any, _customer?: any): boolean {
  if (!cardNumber) {
    return false
  }

  // Common test card numbers
  const testCards = [
    '4111111111111111', // Visa
    '4000000000000002', // Visa (declined)
    '5555555555554444', // Mastercard
    '2223003122003222', // Mastercard
    '378282246310005', // American Express
    '371449635398431', // American Express
    '6011111111111117', // Discover
    '6011000990139424', // Discover
  ]

  const cleanCardNumber = cardNumber.replace(/\s+/g, '')
  return testCards.includes(cleanCardNumber)
}

export function applyItemFilters(item: any, intents: number[]): number[] {
  // the backend lists the intents a brand single-step path's filters exclude for this item
  const excluded = item?.excluded_intents

  if (!Array.isArray(excluded) || !excluded.length) {
    return intents
  }

  return intents.filter(intent => !excluded.includes(intent))
}

export function getItemIntents(item: any, settings: any, addOrderTrackingIntent: boolean = true): number[] {
  const intents: number[] = []

  if (settings.show_offer_details && item.can_show_offer_details) {
    intents.push(InputType.OfferDetails)
  }
  // Status-matched items get their own single-step path; all other intents are suppressed.
  if (item.is_shipping_processing || item.is_return_pending) {
    intents.push(InputType.ItemStatus)
    if (item.is_shipping_processing && settings.show_shipping_processing) {
      intents.push(InputType.ShippingProcessing)
    } else if (item.is_return_pending && settings.show_return_pending) {
      intents.push(InputType.ReturnPending)
    }
    return applyItemFilters(item, intents)
  }
  if (addOrderTrackingIntent && settings.show_tracking_in_list && item.can_track_shipment) {
    intents.push(InputType.ShipmentTracking)
  }

  if (item.status === ItemStatus.Trial) {
    if (item.can_cancel_trial) {
      if (isInitialHoldStatus(item)) {
        if (settings.allow_cancel_trial) {
          intents.push(InputType.CancelTrialHold)
        }
      } else {
        if (settings.allow_cancel_subscription_trial) {
          intents.push(InputType.CancelTrial)
        }
      }
    }
    if (settings.allow_cancel_subscription_trial && item.can_return) {
      intents.push(InputType.ReturnTrial)
    }
  } else {
    if (item.can_change_variant) {
      intents.push(InputType.ChangeVariant)
    }
    if (settings.allow_revision && item.can_revise_package) {
      intents.push(InputType.ChangeProduct)
    }
    if (isRecurring(item)) {
      if (settings.allow_edit_billing_interval && item.can_change_billing_interval) {
        intents.push(InputType.ChangeInterval)
      }
      if (item.can_change_subscription_variant) {
        intents.push(InputType.ChangeSubscriptionVariant)
      }
      if (
        settings.allow_revise_subscription &&
        (item.can_revise_subscription_package || item.can_change_subscription_product)
      ) {
        intents.push(InputType.ChangeSubscriptionProduct)
      }
      if (settings.allow_reactivate_subscription && item.can_reactivate_subscription) {
        intents.push(InputType.ReactivateSubscription)
      }
      if ((settings.allow_pause_subscription || settings.allow_skip_shipment) && item.can_pause_subscription) {
        intents.push(InputType.SkipSubscription)
        intents.push(InputType.PauseSubscription)
      }
      if (
        settings.allow_cancel_subscription &&
        item.can_cancel_subscription &&
        item.billing_cycle >= settings.min_billing_cycle_for_cancel_subscription
      ) {
        intents.push(InputType.CancelSubscription)
      }
      if (settings.allow_cancel && item.can_cancel && item.billing_cycle >= settings.min_billing_cycle_for_cancel) {
        if (item.billing_cycle > 1) {
          intents.push(InputType.CancelRebillHold)
        } else {
          intents.push(InputType.CancelSubscriptionHold)
        }
      }
    } else {
      if (settings.allow_cancel && item.can_cancel) {
        intents.push(InputType.Cancel)
      }
    }
    if (item.can_return) {
      if (isRecurring(item) && item.status === ItemStatus.Active) {
        if (settings.allow_return_subscription) {
          intents.push(InputType.ReturnSubscription)
        }
      } else if (settings.allow_return) {
        intents.push(InputType.Return)
      }
    }
  }

  const filteredIntents = applyItemFilters(item, intents)

  // Fallback: an item with no other actionable intents still gets a "View Status" button. Added
  // after filtering, so Item Status is never filterable.
  if (filteredIntents.length === 0) {
    filteredIntents.push(InputType.ItemStatus)
  }

  return filteredIntents
}
